import { useEffect, useRef, useState } from 'react'

// Post-solve retrospective ("aha") follow-up, shown only after a SOLVED test-mode puzzle
// once the retro regime is active (see retroConfig). Two steps:
//   1. Neutral gate — "sudden click" vs "worked steadily". This one tap is the
//      insight-vs-analytic label, and it avoids presupposing an aha (which would induce
//      confabulation). Skippable.
//   2. Only if "it clicked" — show what they actually said while solving (grounds the
//      answer, cutting reconstruction) and ask what changed. Spoken (mic stays open,
//      shown live via `spokenAnswer`) with a typed fallback. Skippable.
// The parent owns the mic + spoken-transcript capture; this component is presentational
// and reports the gate answer + any typed text back through onDone.

export type RetroAnswer = {
  solveFelt: 'sudden' | 'steady' | null
  changedText: string | null
  skipped: boolean
}

export const RetroScreen = ({
  numbers,
  committedTranscript,
  spokenAnswer,
  interimAnswer,
  commitSignal,
  onDone,
}: {
  numbers: number[]
  committedTranscript: string
  spokenAnswer: string
  interimAnswer: string
  commitSignal: number // bumped by the parent when "commit" is heard
  onDone: (answer: RetroAnswer) => void
}) => {
  const [step, setStep] = useState<'gate' | 'changed'>('gate')
  const [felt, setFelt] = useState<'sudden' | 'steady' | null>(null)
  const [typed, setTyped] = useState('')
  const prevSignal = useRef(commitSignal)

  // Saying "commit" submits — but only once we're on the description step. We consume the
  // signal in every case so a "commit" said during the tap step can't retro-fire later.
  useEffect(() => {
    if (commitSignal === prevSignal.current) return
    prevSignal.current = commitSignal
    if (step === 'changed') onDone({ solveFelt: felt, changedText: typed.trim() || null, skipped: false })
  }, [commitSignal, step, felt, typed, onDone])

  return (
    <div className="flex flex-col items-center px-6 pt-12 pb-10 text-center">
      <div className="w-full max-w-lg">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">A quick reflection</p>

        <div className="mt-3 flex justify-center gap-2">
          {numbers.map((n, i) => (
            <div
              key={i}
              className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#820458]/10 text-sm font-bold text-[#820458]"
            >
              {n}
            </div>
          ))}
        </div>

        {step === 'gate' && (
          <>
            <h2 className="mt-6 text-xl font-semibold text-gray-800">How did solving that feel?</h2>
            <p className="mt-2 text-gray-600">
              Did the answer <span className="font-semibold">suddenly click</span>, or did you{' '}
              <span className="font-semibold">work steadily</span> to it?
            </p>
            <div className="mt-6 grid gap-3">
              <button
                type="button"
                onClick={() => {
                  setFelt('sudden')
                  setStep('changed')
                }}
                className="w-full rounded-lg bg-[#820458] py-3 font-semibold text-white"
              >
                💡 It suddenly clicked
              </button>
              <button
                type="button"
                onClick={() => {
                  setFelt('steady')
                  setStep('changed')
                }}
                className="w-full rounded-lg bg-[#820458] py-3 font-semibold text-white"
              >
                🧭 I worked steadily to it
              </button>
            </div>
            <button
              type="button"
              onClick={() => onDone({ solveFelt: null, changedText: null, skipped: true })}
              className="mt-3 text-sm text-gray-400 underline"
            >
              Skip
            </button>
          </>
        )}

        {step === 'changed' && (
          <>
            <h2 className="mt-6 text-xl font-semibold text-gray-800">How did you get there?</h2>
            <p className="mt-2 text-gray-600">
              Walk us through it — what were you stuck on, and what changed so you could solve it? Say it out loud,
              or type below.
            </p>

            {committedTranscript && (
              <div className="mt-4 text-left">
                <div className="text-xs text-gray-400">What you said while solving</div>
                <div className="mt-1 max-h-28 overflow-y-auto rounded-lg bg-gray-50 px-3 py-2 text-sm leading-relaxed text-gray-600">
                  {committedTranscript}
                </div>
              </div>
            )}

            <div className="mt-4 text-left">
              <div className="text-xs text-gray-400">Your reflection {spokenAnswer || interimAnswer ? '(listening…)' : ''}</div>
              <div className="mt-1 min-h-[2.5rem] rounded-lg bg-[#398874]/5 px-3 py-2 text-left text-sm leading-relaxed">
                {spokenAnswer && <span className="text-gray-700">{spokenAnswer}</span>}
                {interimAnswer && <span className="text-gray-400 italic"> {interimAnswer}</span>}
                {!spokenAnswer && !interimAnswer && (
                  <span className="text-gray-400 italic">Start talking, or type below…</span>
                )}
              </div>
            </div>

            <textarea
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Or type what changed…"
              className="mt-3 h-20 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#820458] focus:outline-none"
            />

            <button
              type="button"
              onClick={() => onDone({ solveFelt: felt, changedText: typed.trim() || null, skipped: false })}
              className="mt-4 w-full rounded-lg bg-[#820458] py-3 font-semibold leading-tight text-white"
            >
              Commit
              <span className="block text-sm font-normal opacity-90">(or just say “commit”)</span>
            </button>
            <button
              type="button"
              onClick={() => onDone({ solveFelt: felt, changedText: typed.trim() || null, skipped: true })}
              className="mt-3 text-sm text-gray-400 underline"
            >
              Skip
            </button>
          </>
        )}
      </div>
    </div>
  )
}
